import { ChallengeSelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ModalSteps, StepConfig, useSteps } from '@components/shared/modal-steps.component';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@components/ui/alert-dialog';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useCreateChallenge, useDeleteChallenge, useUpdateChallenge } from '@queries/challenges.query';
import { routes } from '@routes';
import { challengeSchema, detailsStepSchema, phasesStepSchema } from '@schemas/challenge.schema';
import { useStore } from '@tanstack/react-form';
import { createFormInvalidHandler } from '@utils/form.util';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { challengeFormOptions } from '../challenge-form-options';
import { ChallengeDetailsStep } from './challenge-details-step.component';
import { ChallengePhasesStep } from './challenge-phases-step.component';

type ChallengeModalProps = {
  challenge?: ChallengeSelectDto;
  initialStep?: ChallengeModalStep;
  isEdit: boolean;
  isOpen: boolean;
  onClose: () => void;
};

export enum ChallengeModalStep {
  DETAILS = 0,
  PHASES = 1,
}

const stepData: StepConfig[] = [
  {
    guidance: 'Give your challenge a descriptive name and optional description',
    label: 'Details',
    step: ChallengeModalStep.DETAILS,
  },
  {
    guidance: 'Each phase can have its own criteria. Phases build on previous phases cumulatively.',
    label: 'Phases',
    step: ChallengeModalStep.PHASES,
  },
];

const stepSchemaMap = {
  [ChallengeModalStep.DETAILS]: detailsStepSchema,
  [ChallengeModalStep.PHASES]: phasesStepSchema,
};

export const ChallengeModal: FC<ChallengeModalProps> = (props) => {
  const { challenge, initialStep: requestedInitialStep, isEdit, isOpen, onClose } = props;
  const initialStep = isEdit ? (requestedInitialStep ?? ChallengeModalStep.DETAILS) : ChallengeModalStep.DETAILS;
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const navigate = useNavigate();

  const { isPending: isCreating, mutateAsync: createChallenge } = useCreateChallenge();
  const { isPending: isUpdating, mutateAsync: updateChallenge } = useUpdateChallenge();
  const { isPending: isDeleting, mutateAsync: deleteChallenge } = useDeleteChallenge();

  // Form default values function
  const getFormDefaultValues = () => {
    if (isEdit && challenge) {
      return {
        challengePhases: challenge.phases.map((phase) => ({
          criteria: phase.criteria.map((criterion) => {
            // Transform DTO values back to form format
            // Single-value operators expect string, multi-value operators expect array
            const isSingleValueOperator = ['IS', 'IS_NOT', 'GREATER_THAN', 'LESS_THAN', 'EQUALS'].includes(
              criterion.operator,
            );

            let formValues: unknown = criterion.values;

            // Convert array to string for single-value operators
            if (isSingleValueOperator && Array.isArray(criterion.values) && criterion.values.length > 0) {
              formValues = criterion.values[0];
            }

            // Convert boolean to string for boolean criteria types
            if (typeof formValues === 'boolean') {
              formValues = formValues ? 'true' : 'false';
            }

            return {
              operator: criterion.operator,
              type: criterion.type,
              values: formValues,
            };
          }) as (typeof challengeFormOptions.defaultValues.challengePhases)[number]['criteria'],
          name: phase.name,
        })),
        description: challenge.description || '',
        name: challenge.name,
      };
    }

    return challengeFormOptions.defaultValues;
  };

  const form = useAppForm({
    ...challengeFormOptions,
    canSubmitWhenInvalid: true,
    defaultValues: getFormDefaultValues(),
    onSubmit: async ({ value }) => {
      if (step < ChallengeModalStep.PHASES && !isEdit) {
        updateStepState(step);
        setStep(step + 1);
        return;
      }

      const validationResult = challengeSchema.safeParse(value);
      if (!validationResult.success) {
        errorToast({ description: 'Please fill in all required fields correctly.' });
        return;
      }

      try {
        if (isEdit) {
          if (!challenge) return;
          await updateChallenge({ challengeId: challenge.id, ...validationResult.data });
          successToast({
            description: 'Challenge updated successfully',
          });
          onCloseOverride();
        } else {
          const newChallenge = await createChallenge(validationResult.data);
          successToast({
            description: 'Challenge created successfully',
          });
          onCloseOverride();
          navigate(`/tech-challenges/${newChallenge.id}`);
        }
      } catch (error) {
        console.error(`Failed to ${isEdit ? 'update' : 'create'} challenge:`, error);
        errorToast({
          description: `Failed to ${isEdit ? 'update' : 'create'} challenge`,
        });
      }
    },
    onSubmitInvalid: createFormInvalidHandler(),
    validators: {
      onSubmit: ({ formApi }) => {
        const currentStepSchema = stepSchemaMap[step];
        const errors = formApi.parseValuesWithSchema(currentStepSchema);
        if (errors) return errors;
      },
    },
  });

  const {
    handleStepClick: handleStepClickBase,
    resetSteps,
    setStep,
    step,
    stepsInfo,
    updateStepState,
  } = useSteps(stepData, initialStep, isEdit);
  const handleKeyDown = useCallback((e: KeyboardEvent): void => {
    const isWithinCommandOrPopover = (e.target as HTMLElement)?.closest('[role="combobox"]');

    if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement || isWithinCommandOrPopover)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);
  const resetForm = (): void => {
    form.reset();
    resetSteps();
  };

  const onCloseOverride = (): void => {
    resetForm();
    onClose();
  };

  const formErrors = useStore(form.store, (state) => state.errors);

  const handleBack = (): void => {
    if (formErrors.length > 0) {
      errorToast({ description: 'Please fill in all required fields correctly.' });
      return;
    }

    setStep((prevStep) => prevStep - 1);
  };

  const handleStepClick = (targetStep: ChallengeModalStep): void => {
    if (formErrors.length > 0) {
      errorToast({ description: 'Please fill in all required fields correctly.' });
      return;
    }

    handleStepClickBase(targetStep);
  };

  const handleDelete = async (): Promise<void> => {
    if (!isEdit || !challenge) return;

    await deleteChallenge(challenge.id, {
      onSuccess: () => {
        navigate(routes.techChallenges.path);
      },
    });
  };

  const currentStepData = stepData.find((data) => data.step === step);

  return (
    <Modal
      footer={{
        leftButton: isEdit
          ? {
              disabled: isUpdating,
              loading: isDeleting,
              onClick: () => setIsAlertOpen(true),
              children: 'Delete',
              variant: 'destructive',
            }
          : step !== ChallengeModalStep.DETAILS
            ? {
                onClick: handleBack,
                children: 'Back',
                variant: 'secondary',
              }
            : undefined,
        rightButton: {
          disabled: isDeleting,
          loading: isCreating || isUpdating,
          onClick: form.handleSubmit,
          children: isEdit ? 'Update' : step === ChallengeModalStep.PHASES ? 'Create Challenge' : 'Continue',
        },
      }}
      header={{ title: `${isEdit ? 'Edit' : 'Create New'} Challenge` }}
      isOpen={isOpen}
      onClose={onCloseOverride}
      width="800px"
    >
      {isEdit && (
        <AlertDialog
          open={isAlertOpen}
          onOpenChange={setIsAlertOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the challenge.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                variant="danger"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <div className="flex flex-col gap-4 py-4">
        <ModalSteps
          onStepClick={handleStepClick}
          stepsInfo={stepsInfo}
        />

        <div className="flex flex-col px-4 gap-4">
          {currentStepData?.guidance && (
            <div className="text-md font-normal text-gray-500 whitespace-pre-wrap">{currentStepData?.guidance}</div>
          )}

          <div className="h-full w-full">
            {step === ChallengeModalStep.DETAILS && <ChallengeDetailsStep form={form} />}
            {step === ChallengeModalStep.PHASES && <ChallengePhasesStep form={form} />}
          </div>
        </div>
      </div>
    </Modal>
  );
};
